import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSupplyUnitLabel, supplyUnitEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailySupplyMutation, useUpdateDailySupplyMutation } from '../../../../hooks/mutations/useDailySupplyMutation';
import { useCompaniesQuery } from '../../../../hooks/queries/useCompaniesQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DailySupply } from '../../../../services/apiSLTP/dailyReports/dailySupplies';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailySupplyModalProps extends ModalProps {
    refetch: () => void;
    dailySupply: DailySupply | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailySupplyModal: React.FC<ManageValidationDailySupplyModalProps> = ({
    open,
    handleClose,
    activities,
    refetch,
    dailySupply,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailySupply?.activityId)?.label || '',
        value: dailySupply?.activityId || '',
    });
    const [currentSupplyId, setCurrentSupplyId] = useState(dailySupply?._id || '');
    const [designation, setDesignation] = useState(dailySupply?.designation || '');
    const [company, setCompany] = useState<OptionType | null>(null);
    const [quantity, setQuantity] = useState(dailySupply?.quantity.toString() || '');
    const [unit, setUnit] = useState<OptionType | null>(
        dailySupply?.unit ? { label: capitalizeFirstLetter(t(getSupplyUnitLabel(dailySupply.unit))), value: dailySupply.unit.toString() } : null
    );
    const [observations, setObservations] = useState(dailySupply?.observations || '');
    const [price, setPrice] = useState(dailySupply?.unitPrice?.toString() || '');
    const [orderNumber, setOrderNumber] = useState(dailySupply?.orderNumber || '');

    const { data: dataCompanies, isLoading: isCompaniesLoading } = useCompaniesQuery({ queries: { active: true } });

    const { mutate: createDailySupply } = useCreateDailySupplyMutation({
        onSuccess: (data) => {
            refetch();
            setCurrentSupplyId(data.data._id);
        },
    });
    const { mutate: updateDailySupply } = useUpdateDailySupplyMutation({ onSuccess: () => refetch() });

    const handleCreate = async () => {
        createDailySupply({
            activityId: selectedActivity.value,
            designation,
            quantity: parseFloat(quantity),
            unit: parseFloat(unit?.value || ''),
            companyId: company?.value || '',
            observations: observations?.trim(),
            unitPrice: price ? parseFloat(price) : undefined,
            orderNumber: orderNumber?.trim(),
        });
    };

    const handleUpdate = async () => {
        if (!currentSupplyId) return;

        updateDailySupply({
            id: currentSupplyId,
            data: {
                designation,
                quantity: parseFloat(quantity),
                unit: parseFloat(unit?.value || ''),
                observations: observations?.trim(),
                unitPrice: price ? parseFloat(price) : null,
                orderNumber: orderNumber?.trim(),
            },
        });
    };

    useEffect(() => {
        const tmpCompany = dataCompanies?.find((company) => company._id === dailySupply?.companyId._id);
        if (dailySupply && tmpCompany) {
            setCompany({ label: tmpCompany.designation, value: tmpCompany._id });
        }
    }, [dataCompanies]);

    return (
        <ModalBody
            title={t('supply')}
            subtitle={!!currentSupplyId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                currentSupplyId.length ? handleUpdate() : handleCreate();
            }}
            disableSubmit={!(designation && quantity.length && unit)}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <CustomTextInput value={designation} onChange={setDesignation} label={t('name')} required />
            <DropDown
                selectedItem={company}
                items={dataCompanies?.map((company) => ({ label: company.designation, value: company._id })) || []}
                handleChange={(event, value) => setCompany(value)}
                inputLabel={t('company')}
                required
            />
            <DropDown
                selectedItem={unit}
                items={Object.values(supplyUnitEnum).map((unit) => ({
                    label: capitalizeFirstLetter(t(getSupplyUnitLabel(unit))),
                    value: unit.toString(),
                }))}
                handleChange={(event, value) => setUnit(value)}
                inputLabel={t('unit')}
                required
            />
            <CustomTextInput value={quantity} onChange={setQuantity} label={t('quantity')} type="number" />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('unitPrice')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <CustomTextInput value={orderNumber} onChange={setOrderNumber} label={t('orderNumber')} />
            <CustomTextInput value={observations} onChange={setObservations} label={t('observations')} multiline />
        </ModalBody>
    );
};

export default ManageValidationDailySupplyModal;
